// Chaves PIX do seller, canvas Zenith `screens/seller/chaves-pix.tsx` (WEB-111, port de
// WEB-62).
//
// Foco da tela: o DOCUMENTO do KYC. Ele é a chave (`spec/09` §4.1, FIX-PIX-LOOKUP), então
// o card do documento vem antes da tabela e o resto é consequência dele.
//
// O estado da adquirente é POR LINHA (`partner_status`, API-29), não mais um `Notice`
// agregado de `counters`: uma chave recusada só aparece na linha dela.
//
// Quem tem `useQuery`/`useMutation` é esta rota; os componentes de `features/pix-keys`
// recebem dado por prop, inclusive o perfil, que antes o dialog de cadastro buscava por
// conta própria (item 4 das "Questões abertas" de FIX-PIX-LOOKUP, agora fechado).
//
// O toggle de modo mora no Shell (WEB-116), não mais no `PageHeader` desta tela.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { KeyRound } from 'lucide-react';
import { useState } from 'react';
import { profileQueryOptions } from '../api/queries/account.ts';
import {
  type CreatePixKeyBody,
  createPixKey,
  deactivatePixKey,
  PIX_KEYS_QUERY_KEY,
  type PixKey,
  pixKeysQueryOptions,
} from '../api/queries/pix-keys.ts';
import { countersQueryOptions } from '../api/queries/shell.ts';
import { useMode } from '../app/mode.tsx';
import { EmptyState } from '../components/app/empty-state.tsx';
import { ListSkeleton } from '../components/app/list-skeleton.tsx';
import { Notice } from '../components/app/notice.tsx';
import { PageHeader } from '../components/app/page-header.tsx';
import { Button } from '../components/ui/button.tsx';
import { Card, CardContent } from '../components/ui/card.tsx';
import { Skeleton } from '../components/ui/skeleton.tsx';
import { CreatePixKeyDrawer } from '../features/pix-keys/create-pix-key-drawer.tsx';
import { KycDocumentCard } from '../features/pix-keys/kyc-document-card.tsx';
import {
  activeCount,
  createBlockedReason,
  createErrorMessage,
  isStepUpCancelled,
  MAX_ACTIVE_PIX_KEYS,
} from '../features/pix-keys/model.ts';
import { type PixKeyRowError, PixKeysTable } from '../features/pix-keys/pix-keys-table.tsx';
import { RemovePixKeyDrawer } from '../features/pix-keys/remove-pix-key-drawer.tsx';

function PixKeys() {
  const { mode } = useMode();
  const queryClient = useQueryClient();
  const keys = useQuery(pixKeysQueryOptions(mode));
  const profile = useQuery(profileQueryOptions(mode));

  const [createOpen, setCreateOpen] = useState(false);
  const [toRemove, setToRemove] = useState<PixKey | undefined>(undefined);
  const [rowError, setRowError] = useState<PixKeyRowError | undefined>(undefined);

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [PIX_KEYS_QUERY_KEY] }),
      queryClient.invalidateQueries({ queryKey: countersQueryOptions(mode).queryKey }),
    ]);
  };

  const create = useMutation({
    mutationFn: async (body: CreatePixKeyBody) => createPixKey(mode, body),
    onSuccess: async () => {
      setCreateOpen(false);
      await refresh();
    },
  });

  const remove = useMutation({
    mutationFn: async (key: PixKey) => {
      setRowError(undefined);
      return deactivatePixKey(mode, key.id);
    },
    onSuccess: () => {
      setToRemove(undefined);
    },
    onError: (error, key) => {
      setToRemove(undefined);
      if (!isStepUpCancelled(error)) {
        setRowError({ keyId: key.id, message: createErrorMessage(error) });
      }
    },
    onSettled: refresh,
  });

  const rows = keys.data ?? [];
  const blocked = createBlockedReason(profile.data, rows);

  // Frames "Carregando" e "Erro" do canvas: enquanto não há dado nenhum, a tela inteira
  // vira o placeholder ou o aviso, sem documento nem tabela pela metade.
  const isLoading = profile.data === undefined || keys.isPending;
  // Erro só quando não há lista: com o polling de 30 s, um flake num refetch de fundo
  // trocaria a lista carregada por uma tela de falha.
  const isError = keys.data === undefined && keys.error !== null && keys.error !== undefined;

  const openCreate = (): void => {
    create.reset();
    setCreateOpen(true);
  };

  return (
    <>
      <PageHeader
        title="Chaves PIX"
        description="A chave de saque é o documento do seu cadastro, sem consulta a banco"
      >
        {!isLoading && !isError && (
          <Button disabled={blocked !== null} onClick={openCreate}>
            Cadastrar chave
          </Button>
        )}
      </PageHeader>

      {isError ? (
        <Notice
          tone="danger"
          title="Não foi possível carregar as chaves PIX."
          sub="Tente de novo em instantes."
          action={
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                void keys.refetch();
              }}
            >
              Tentar de novo
            </Button>
          }
        />
      ) : profile.data === undefined || keys.isPending ? (
        <>
          <Card>
            <CardContent>
              <Skeleton className="h-14 rounded-xl" />
            </CardContent>
          </Card>
          <ListSkeleton />
        </>
      ) : (
        <>
          <KycDocumentCard profile={profile.data} />

          {/* O motivo do bloqueio é TEXTO na tela, não só o `disabled` do botão: um botão
              apagado sem explicação é a tela escondendo a razão (`spec/17` §7 item 12). */}
          {blocked?.kind === 'alert' && (
            <Notice
              tone={blocked.tone}
              title={blocked.reason}
              sub="A chave PIX é o CPF/CNPJ aprovado na verificação, você não escolhe nem digita chave."
            />
          )}
          {blocked?.kind === 'quiet' && (
            <p className="-mt-1 text-xs text-muted-foreground">{blocked.reason}</p>
          )}

          <Notice
            tone="info"
            title="Toda ação aqui exige step-up."
            sub="Senha atual ou código do app autenticador, sem exceção, mesmo com a 2FA desligada."
          />

          {rows.length === 0 ? (
            <EmptyState
              icon={KeyRound}
              title="Nenhuma chave PIX cadastrada"
              description="O saque só sai para a chave do documento aprovado no seu KYC. Cadastre-a para liberar o saque."
              action={
                blocked === null ? (
                  <Button onClick={openCreate}>Cadastrar chave</Button>
                ) : (
                  <p className="text-xs text-muted-foreground">{blocked.reason}</p>
                )
              }
            />
          ) : (
            <div>
              <div className="mb-2.5 flex flex-col gap-0.5">
                <h2 className="text-sm font-medium text-foreground">Suas chaves</h2>
                <span className="text-xs text-muted-foreground">
                  {activeCount(rows)} de {MAX_ACTIVE_PIX_KEYS} ativas · o saque só sai para chave
                  aprovada pela adquirente
                </span>
              </div>
              <PixKeysTable
                keys={rows}
                onRemove={setToRemove}
                {...(rowError === undefined ? {} : { rowError })}
              />
            </div>
          )}
        </>
      )}

      <CreatePixKeyDrawer
        open={createOpen}
        onOpenChange={setCreateOpen}
        profile={profile.data}
        isPending={create.isPending}
        error={create.error}
        onSubmit={(body) => {
          create.mutate(body);
        }}
      />

      <RemovePixKeyDrawer
        pixKey={toRemove}
        onOpenChange={(open) => {
          if (!open) {
            setToRemove(undefined);
          }
        }}
        isPending={remove.isPending}
        onConfirm={(key) => {
          remove.mutate(key);
        }}
      />
    </>
  );
}

export const Route = createFileRoute('/app/settings/pix-keys/')({ component: PixKeys });
